import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  PenSquare, Search, Send, Loader2, Link as LinkIcon, X, Plus, Image as ImageIcon, Upload, FileText, Eye, EyeOff,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PasteImageButton from "@/components/PasteImageButton";
import SourceLimitNote from "@/components/SourceLimitNote";
import SourceDateTag from "@/components/SourceDateTag";
import {
  customArticleReserved, fullMessage, overflowUrls, reservedLabel, urlCapacity,
} from "@/lib/sourceLimit";
import { tr, uiLocale } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

// Mirrors backend source_clip.EDITOR_SOURCE_MAX_CHARS — the most of any
// one attached document the writer will receive.
const EDITOR_SOURCE_MAX_CHARS = 50000;

function formatError(detail) {
  if (!detail) return tr("Something went wrong. Please try again.");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function CustomArticlePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [headline, setHeadline] = useState("");
  const [angle, setAngle] = useState("");
  const [notes, setNotes] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [urls, setUrls] = useState([]);            // string[]
  // "Force Firecrawl on sources" — set by the editor when they know a
  // URL bot-walls (e.g. oem.com.mx). Skips the httpx-first step and
  // sends every URL through Firecrawl on the first attempt. Costs
  // Firecrawl credits per URL.
  const [forceFirecrawl, setForceFirecrawl] = useState(false);
  // "Fetch full article text" — ADMIN ONLY. Raises the per-URL scrape
  // ceiling from 6,000 to 30,000 characters. The backend also rejects
  // it (403) for non-admins, so hiding the checkbox isn't the only guard.
  const [fullFetch, setFullFetch] = useState(false);
  const [images, setImages] = useState([]);        // { url, name, bytes }[]
  const [customPrompt, setCustomPrompt] = useState("");
  const [writerKey, setWriterKey] = useState("");
  const [suggestedSources, setSuggestedSources] = useState([]);
  // Editor-uploaded documents (press releases, transcripts, briefs).
  // Each: { filename, text, chars, method, ocr_used, expanded }.
  const [attachedDocs, setAttachedDocs] = useState([]);

  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);

  const { data: writers } = useQuery({
    queryKey: ["writers"],
    queryFn: api.listWriters,
  });

  const writerOptions = useMemo(() => {
    if (!writers) return [];
    // /api/writers returns an array of { key, name, ... }.
    // (Older versions of this code assumed a keyed dict — hence the "Unknown
    //  writer: 0" bug when the numeric index was sent instead of the key.)
    if (Array.isArray(writers)) {
      return writers
        .filter((w) => w && w.key)
        .map((w) => ({ key: w.key, name: w.name || w.key }));
    }
    return Object.entries(writers).map(([k, w]) => ({
      key: k,
      name: w?.name || k,
    }));
  }, [writers]);

  // Default the writer to the first one once loaded
  useEffect(() => {
    if (!writerKey && writerOptions.length > 0) {
      setWriterKey(writerOptions[0].key);
    }
  }, [writerKey, writerOptions]);

  // Writer source limit (lib/sourceLimit.js): the editor's brief
  // (headline or notes) and each attached document go to the writer
  // FIRST, so URLs get what's left of the 8 slots.
  const reservedSlots = customArticleReserved({
    headline, notes, docCount: attachedDocs.length,
  });
  const urlSlots = urlCapacity(reservedSlots);
  const overflow = overflowUrls(urls, reservedSlots);

  // Returns true when the URL was added, so the suggestions picker
  // only drops a row that actually made it onto the list.
  const addUrl = (raw) => {
    const u = (raw || "").trim();
    if (!u) return false;
    try {
      const parsed = new URL(u);
      const clean = parsed.toString().replace(/#$/, "");
      if (urls.includes(clean)) {
        toast.error(tr("Already added"));
        return false;
      }
      if (urls.length >= urlSlots) {
        toast.error(fullMessage());
        return false;
      }
      setUrls([...urls, clean]);
      setUrlInput("");
      return true;
    } catch {
      toast.error(tr("Please enter a valid URL (starting with http:// or https://)"));
      return false;
    }
  };

  const removeUrl = (u) => setUrls(urls.filter((x) => x !== u));

  const uploadImageMutation = useMutation({
    mutationFn: async (file) => {
      const res = await api.customArticleUploadImage(file);
      return { ...res, name: file.name };
    },
    onSuccess: (data) => {
      setImages((prev) => [...prev, data]);
      toast.success(tr("Image uploaded ({round} KB, resized to WebP)", { round: Math.round(data.bytes / 1024) }));
    },
    onError: (e) =>
      toast.error(formatError(e?.response?.data?.detail) || e.message),
  });

  const onFileChosen = (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (!f.type.startsWith("image/")) {
        toast.error(tr("Skipping {name}: not an image", { name: f.name }));
        continue;
      }
      if (f.size > 25 * 1024 * 1024) {
        toast.error(tr("Skipping {name}: larger than 25 MB", { name: f.name }));
        continue;
      }
      uploadImageMutation.mutate(f);
    }
    e.target.value = "";
  };

  const removeImage = (url) => setImages(images.filter((i) => i.url !== url));

  const extractDocMutation = useMutation({
    mutationFn: (file) => api.customArticleExtractFile(file),
    onSuccess: (data) => {
      setAttachedDocs((prev) => [...prev, { ...data, expanded: false }]);
      const kb = Math.round(data.chars / 1024);
      const ocrTag = data.ocr_used ? tr(" · OCR") : "";
      toast.success(tr("Extracted {value} chars ({kb} KB) from {filename}{ocrTag}", { value: data.chars.toLocaleString(uiLocale()), kb, filename: data.filename, ocrTag }));
    },
    onError: (e) =>
      toast.error(formatError(e?.response?.data?.detail) || e.message),
  });

  const onDocsChosen = (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const name = (f.name || "").toLowerCase();
      const isSupported =
        name.endsWith(".txt") ||
        name.endsWith(".md") ||
        name.endsWith(".docx") ||
        name.endsWith(".pdf");
      if (!isSupported) {
        toast.error(tr("Skipping {name}: use .txt, .docx, or .pdf", { name: f.name }));
        continue;
      }
      if (f.size > 15 * 1024 * 1024) {
        toast.error(tr("Skipping {name}: larger than 15 MB", { name: f.name }));
        continue;
      }
      extractDocMutation.mutate(f);
    }
    e.target.value = "";
  };

  const removeDoc = (filename) =>
    setAttachedDocs((prev) => prev.filter((d) => d.filename !== filename));

  const toggleDocPreview = (filename) =>
    setAttachedDocs((prev) =>
      prev.map((d) =>
        d.filename === filename ? { ...d, expanded: !d.expanded } : d
      )
    );

  const findMoreMutation = useMutation({
    mutationFn: () =>
      api.customArticleFindMoreSources({
        headline: headline.trim(),
        notes,
        angle,
        existing_urls: urls,
      }),
    onSuccess: (data) => {
      setSuggestedSources(data.new_sources || []);
      const stats = data.decode_stats || {};
      const decoderTotallyBroken =
        (stats.attempted || 0) > 0 && (stats.decoded || 0) === 0;
      if ((data.new_sources || []).length === 0) {
        // See IdeasList.jsx — swap the misleading "refine the angle"
        // message when the decoder is the actual failing component.
        if (decoderTotallyBroken) {
          toast.error(
            tr("Source lookup is failing to resolve Google News links — check server logs"),
            { duration: 8000 },
          );
        } else {
          toast.info(tr("No new sources found — try refining the headline or notes"));
        }
      } else {
        toast.success(tr("Found {new_sourceCount} more sources", { new_sourceCount: data.new_sources.length }));
        if ((stats.failed || 0) > 0) {
          toast.message(
            tr("{failed} source{s} could not be fully resolved", { failed: stats.failed, s: stats.failed === 1 ? "" : "s" }),
            { duration: 6000 },
          );
        }
      }
    },
    onError: (e) =>
      toast.error(formatError(e?.response?.data?.detail) || e.message),
  });

  const generateMutation = useMutation({
    mutationFn: () =>
      api.createCustomArticleAndWait({
        headline: headline.trim(),
        angle: angle.trim(),
        notes,
        urls,
        image_urls: images.map((i) => i.url),
        attached_documents: attachedDocs.map((d) => ({
          filename: d.filename,
          text: d.text,
        })),
        writer_key: writerKey,
        custom_prompt: customPrompt.trim() || null,
        force_firecrawl: forceFirecrawl,
        full_fetch: isAdmin && fullFetch,
      }),
    onSuccess: (article) => {
      if (!article?.id) {
        toast.error(tr("Article was written but no ID returned — check the Library"));
        return;
      }
      toast.success(tr("Article written — opening in the Library"));
      navigate(`/article/${article.id}`);
    },
    onError: (e) => {
      // Prompt-only short-circuit: success toast already fired inside
      // the API layer. Don't overlay a red error toast on top.
      if (e?.isPromptOnly) return;
      // Task-loop errors surface as plain Error instances with the
      // backend's `error` string on `e.message`. Axios errors put the
      // real detail on `response.data.detail`. Check both so editors
      // don't just see the generic "Something went wrong" wall.
      const apiDetail = formatError(e?.response?.data?.detail);
      const isGeneric = apiDetail === tr("Something went wrong. Please try again.");
      const msg = isGeneric && e?.message ? e.message : apiDetail;
      toast.error(msg);
    },
  });

  // At least ONE meaningful input is required so the LLM has material
  // to work from. Any of:  a source URL, a headline, or notes.
  // Writer is always required.
  const canGenerate =
    !!writerKey &&
    (urls.length > 0 ||
      headline.trim().length >= 3 ||
      notes.trim().length >= 10 ||
      attachedDocs.length > 0) &&
    !generateMutation.isPending;

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-3xl mx-auto px-4 sm:px-10 py-8 sm:py-12 space-y-10">
        <header className="border-b border-[var(--border)] pb-6">
          <div className="overline text-[var(--ink-muted)] mb-2 flex items-center gap-2">
            <PenSquare className="w-3.5 h-3.5 text-[var(--accent)]" />{tr("newsroom · write your own")}</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight">{tr("Write a custom article")}</h1>
          <p className="mt-3 text-sm text-[var(--ink-muted)]">{tr("Paste what you know — notes, quotes, background, links — and pick a writer. The article will be drafted in the same house style as the rest of the newsroom and land in your Library.")}</p>
        </header>

        {/* Headline */}
        <section className="space-y-2">
          <label className="overline text-[var(--ink-muted)] block">{trRich("Working headline <0>— optional if you provide sources</0>", null, [<span className="normal-case tracking-normal text-[var(--ink-muted)]/80 ml-1" />])}
          </label>
          <input
            data-testid="custom-headline-input"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder={tr("Leave blank to let the LLM headline the piece from your sources")}
            className="w-full border border-[var(--border)] bg-white px-3 py-2.5 text-lg focus:outline-none focus:border-[var(--slate-900)]"
          />
        </section>

        {/* Angle */}
        <section className="space-y-2">
          <label className="overline text-[var(--ink-muted)] block">{trRich("Angle <0>— optional, one line</0>", null, [<span className="normal-case tracking-normal text-[var(--ink-muted)]/80 ml-1" />])}
          </label>
          <input
            data-testid="custom-angle-input"
            value={angle}
            onChange={(e) => setAngle(e.target.value)}
            placeholder={tr("e.g. Why the route change is a win for local businesses")}
            className="w-full border border-[var(--border)] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[var(--slate-900)]"
          />
        </section>

        {/* Notes */}
        <section className="space-y-2">
          <label className="overline text-[var(--ink-muted)] block">{trRich("Notes, quotes, background <0>— optional</0>", null, [<span className="normal-case tracking-normal text-[var(--ink-muted)]/80 ml-1" />])}
          </label>
          <textarea
            data-testid="custom-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={10}
            maxLength={20000}
            placeholder={tr("Paste everything you have — bullet points, quotes, press releases, background context. The writer will use this as the primary source of truth.")}
            className="w-full border border-[var(--border)] bg-white px-3 py-3 text-sm focus:outline-none focus:border-[var(--slate-900)] resize-y"
          />
          <div className="text-[11px] text-[var(--ink-muted)] font-mono-display text-right">
            {notes.length}/20000
          </div>
        </section>

        {/* URLs */}
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className="overline text-[var(--ink-muted)]">{trRich("Source URLs <0>— optional, one at a time</0>", null, [<span className="normal-case tracking-normal text-[var(--ink-muted)]/80 ml-1" />])}
            </label>
            <button
              data-testid="custom-find-more-btn"
              onClick={() => findMoreMutation.mutate()}
              disabled={
                findMoreMutation.isPending || headline.trim().length < 3
              }
              title={
                headline.trim().length < 3
                  ? tr("Enter a working headline first")
                  : tr("Search the web for more relevant sources")
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {findMoreMutation.isPending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />{tr("Scouring the web…")}</>
              ) : (
                <>
                  <Search className="w-3.5 h-3.5" />{tr("Find more sources")}</>
              )}
            </button>
          </div>

          <div className="flex gap-2">
            <input
              data-testid="custom-url-input"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addUrl(urlInput);
                }
              }}
              placeholder={urls.length >= urlSlots ? tr("Source limit reached") : "https://…"}
              disabled={urls.length >= urlSlots}
              className="flex-1 border border-[var(--border)] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[var(--slate-900)] disabled:opacity-60"
            />
            <button
              data-testid="custom-url-add-btn"
              onClick={() => addUrl(urlInput)}
              disabled={!urlInput.trim() || urls.length >= urlSlots}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-50"
            >
              <Plus className="w-3.5 h-3.5" />{tr("Add")}</button>
          </div>

          <SourceLimitNote
            count={urls.length}
            reserved={reservedSlots}
            reservedText={reservedLabel({
              brief: Boolean(headline.trim() || notes.trim()),
              docCount: attachedDocs.length,
              briefName: tr("your headline/notes"),
            })}
            testId="custom-source-limit"
            className="mt-2"
          />

          {urls.length > 0 && (
            <ul className="mt-2 space-y-1">
              {urls.map((u, i) => (
                <li
                  key={u}
                  data-testid={`custom-url-row-${i}`}
                  className="flex items-center gap-2 text-xs bg-white border border-[var(--border)] px-2 py-1.5"
                >
                  <LinkIcon className="w-3 h-3 text-[var(--ink-muted)] shrink-0" />
                  <a
                    href={u}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 break-all text-[var(--accent)] hover:text-[var(--accent-hover)]"
                  >
                    {u}
                  </a>
                  {overflow.includes(u) && (
                    <span
                      data-testid={`custom-url-overflow-${i}`}
                      title={tr("Past the 8-source limit — the writer won't receive this one")}
                      className="text-[9px] uppercase tracking-wider px-1 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 font-mono-display shrink-0"
                    >{tr("not sent")}</span>
                  )}
                  <button
                    data-testid={`custom-url-remove-${i}`}
                    onClick={() => removeUrl(u)}
                    className="text-[var(--ink-muted)] hover:text-red-600 shrink-0"
                    aria-label={tr("Remove URL")}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Force Firecrawl toggle — visible whenever there's at least
              one URL. Useful for sites the editor already knows return
              chrome/nav under a bot-wall. Newsdesk auto-falls back to
              Firecrawl on detection, but this makes it a one-check
              override for known-bad domains. */}
          {urls.length > 0 && (
            <label
              data-testid="custom-force-firecrawl-wrap"
              className="mt-2.5 flex items-start gap-2 text-xs text-[var(--ink-muted)] cursor-pointer select-none"
            >
              <input
                type="checkbox"
                data-testid="custom-force-firecrawl-checkbox"
                checked={forceFirecrawl}
                onChange={(e) => setForceFirecrawl(e.target.checked)}
                className="mt-0.5 accent-[var(--accent)]"
              />
              {trRich("<0><1>Force Firecrawl on sources</1> — skip the fast httpx fetch and scrape every URL through Firecrawl. Use for sites you know bot-wall (like oem.com.mx). Costs one Firecrawl credit per URL.</0>", null, [<span />, <span className="text-[var(--ink)] font-medium" />])}
            </label>
          )}

          {/* Admin-only: raise the per-URL scrape ceiling from 6,000 to
              30,000 chars for long reads. Server enforces admin too. */}
          {isAdmin && urls.length > 0 && (
            <label
              data-testid="custom-full-fetch-wrap"
              className="mt-2 flex items-start gap-2 text-xs text-[var(--ink-muted)] cursor-pointer select-none"
            >
              <input
                type="checkbox"
                data-testid="custom-full-fetch-checkbox"
                checked={fullFetch}
                onChange={(e) => setFullFetch(e.target.checked)}
                className="mt-0.5 accent-[var(--accent)]"
              />
              {trRich("<0><1>Fetch full article text</1> — send up to 30,000 characters per URL instead of 6,000. Use for long reads; may pull in more page clutter (comments, related stories). Admin only.</0>", null, [<span />, <span className="text-[var(--ink)] font-medium" />])}
            </label>
          )}

          {suggestedSources.length > 0 && (
            <div className="mt-3 border border-dashed border-[var(--border)] p-3 bg-white">
              <div className="overline text-[var(--ink-muted)] mb-2">{trRich("Suggested sources · {suggestedSourceCount}<0>— {value}</0>", { suggestedSourceCount: suggestedSources.length, value: urls.length >= urlSlots
                    ? tr("source limit reached")
                    : tr("room for {value} more", { value: urlSlots - urls.length }) }, [<span className="normal-case tracking-normal font-sans ml-1.5" />])}
              </div>
              <ul className="space-y-1.5">
                {suggestedSources.map((s, i) => (
                  <li
                    key={s.url}
                    data-testid={`custom-suggested-source-${i}`}
                    className="flex items-start gap-2 text-xs"
                  >
                    <button
                      onClick={() => {
                        if (addUrl(s.url)) {
                          setSuggestedSources(
                            suggestedSources.filter((x) => x.url !== s.url)
                          );
                        }
                      }}
                      disabled={urls.length >= urlSlots}
                      title={urls.length >= urlSlots ? fullMessage() : undefined}
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-3 h-3" />{tr("Add")}</button>
                    <div className="flex-1 min-w-0">
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="block break-words text-[var(--ink)] hover:text-[var(--accent)]"
                      >
                        {s.title || s.url}
                      </a>
                      <div className="text-[10px] text-[var(--ink-muted)] font-mono-display">
                        {s.source ? `${s.source} · ` : ""}
                        <SourceDateTag
                          publishedAt={s.published_at}
                          testId={`custom-suggested-date-${i}`}
                        />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Attached documents */}
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className="overline text-[var(--ink-muted)]">{trRich("Attach document <0>— optional; .txt, .docx, .pdf (press releases, transcripts)</0>", null, [<span className="normal-case tracking-normal text-[var(--ink-muted)]/80 ml-1" />])}
            </label>
            <input
              ref={docInputRef}
              data-testid="custom-doc-input"
              type="file"
              accept=".txt,.md,.docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              multiple
              onChange={onDocsChosen}
              className="hidden"
            />
            <button
              data-testid="custom-doc-upload-btn"
              onClick={() => docInputRef.current?.click()}
              disabled={extractDocMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-60"
            >
              {extractDocMutation.isPending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />{tr("Extracting…")}</>
              ) : (
                <>
                  <FileText className="w-3.5 h-3.5" />{tr("Attach file(s)")}</>
              )}
            </button>
          </div>

          {attachedDocs.length > 0 && (
            <ul className="space-y-2">
              {attachedDocs.map((d, i) => (
                <li
                  key={d.filename + i}
                  data-testid={`custom-doc-row-${i}`}
                  className="bg-white border border-[var(--border)]"
                >
                  <div className="flex items-center gap-2 px-2.5 py-2 text-xs">
                    <FileText className="w-3.5 h-3.5 text-[var(--ink-muted)] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium">{d.filename}</div>
                      <div className="text-[10px] text-[var(--ink-muted)] font-mono-display mt-0.5">
                        {tr("{value} chars", { value: d.chars.toLocaleString(uiLocale()) })}{d.ocr_used && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 bg-amber-100 text-amber-800 uppercase tracking-wider text-[9px]">{tr("OCR · verify")}</span>
                        )}
                      </div>
                      {d.chars > EDITOR_SOURCE_MAX_CHARS && (
                        <div
                          data-testid={`custom-doc-too-long-${i}`}
                          className="text-[10px] text-amber-700 mt-0.5"
                        >{tr("Only the first {value} characters will be sent to the writer.", { value: EDITOR_SOURCE_MAX_CHARS.toLocaleString(uiLocale()) })}</div>
                      )}
                    </div>
                    <button
                      data-testid={`custom-doc-toggle-${i}`}
                      onClick={() => toggleDocPreview(d.filename)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)]"
                      title={d.expanded ? tr("Hide extracted text") : tr("Preview extracted text")}
                    >
                      {d.expanded ? (
                        <>
                          <EyeOff className="w-3 h-3" />{tr("Hide")}</>
                      ) : (
                        <>
                          <Eye className="w-3 h-3" />{tr("Preview")}</>
                      )}
                    </button>
                    <button
                      data-testid={`custom-doc-remove-${i}`}
                      onClick={() => removeDoc(d.filename)}
                      className="text-[var(--ink-muted)] hover:text-red-600 shrink-0"
                      aria-label={tr("Remove document")}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {d.expanded && (
                    <div className="border-t border-[var(--border)] px-3 py-2 max-h-56 overflow-auto bg-[var(--bg-tint)]/40">
                      <pre className="whitespace-pre-wrap text-[11px] leading-relaxed font-mono-display text-[var(--ink)]">
                        {d.text}
                      </pre>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Images */}
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className="overline text-[var(--ink-muted)]">{trRich("Images <0>— optional; first one becomes WP featured</0>", null, [<span className="normal-case tracking-normal text-[var(--ink-muted)]/80 ml-1" />])}
            </label>
            <input
              ref={fileInputRef}
              data-testid="custom-image-input"
              type="file"
              accept="image/*"
              multiple
              onChange={onFileChosen}
              className="hidden"
            />
            <button
              data-testid="custom-image-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadImageMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-60"
            >
              {uploadImageMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Upload className="w-3.5 h-3.5" />
              )}{tr("Upload image(s)")}</button>
            <PasteImageButton
              onImage={(file) => uploadImageMutation.mutate(file)}
              busy={uploadImageMutation.isPending}
              testId="custom-image-paste-btn"
              title={tr("Paste an image from the clipboard (or press Cmd/Ctrl+V anywhere on this page)")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-60"
            />
          </div>

          {images.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {images.map((im, i) => (
                <div
                  key={im.url}
                  data-testid={`custom-image-tile-${i}`}
                  className="relative group border border-[var(--border)] bg-white"
                >
                  <img
                    src={im.url}
                    alt={im.name}
                    className="w-full aspect-video object-cover"
                  />
                  {i === 0 && (
                    <div className="absolute top-1 left-1 text-[10px] uppercase tracking-wider bg-[var(--accent)] text-white px-1.5 py-0.5 font-mono-display">{tr("featured")}</div>
                  )}
                  <button
                    onClick={() => removeImage(im.url)}
                    className="absolute top-1 right-1 w-5 h-5 bg-white border border-[var(--border)] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-100"
                    aria-label={tr("Remove image")}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Writer + additional prompt */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="overline text-[var(--ink-muted)] block mb-1.5">{trRich("Writer <0>*</0>", null, [<span className="text-red-600" />])}
            </label>
            <select
              data-testid="custom-writer-select"
              value={writerKey}
              onChange={(e) => setWriterKey(e.target.value)}
              className="w-full border border-[var(--border)] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[var(--slate-900)]"
            >
              {writerOptions.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="overline text-[var(--ink-muted)] block mb-1.5">{trRich("Add to prompt <0>— optional</0>", null, [<span className="normal-case tracking-normal text-[var(--ink-muted)]/80 ml-1" />])}
            </label>
            <textarea
              data-testid="custom-add-to-prompt"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder={tr("e.g. Mention that this affects US expats crossing the border.")}
              className="w-full border border-[var(--border)] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[var(--slate-900)] resize-y"
            />
          </div>
        </section>

        {/* Submit */}
        <section className="border-t border-[var(--border)] pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-xs text-[var(--ink-muted)]">{tr("The article will land in your Library with the same tools as any other draft: revise, add internal links, publish to WordPress.")}</div>
          <button
            data-testid="custom-generate-btn"
            data-ai="true"
            onClick={() => generateMutation.mutate()}
            disabled={!canGenerate}
            className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generateMutation.isPending ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />{tr("Writing… (30–60s)")}</>
            ) : (
              <>
                <Send className="w-3.5 h-3.5" />{tr("Write the article")}</>
            )}
          </button>
        </section>
      </div>
    </div>
  );
}
